import { Component, inject, OnDestroy, OnInit, EventEmitter, Output } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { FormControl, Validators } from '@angular/forms';
import { SignrequestService } from '@services/signrequest/signrequest.service';
import { Subscription } from 'rxjs';

import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { AuthenticationMapper } from '@shared/mappers/authentication.mapper';
import { CscProvider, CscUtils } from 'src/app/shared/csc/csc-util';
import {ExclusiveMethodsArray, areMethodsCompatible, AuthMethod, GlobalMethodsArray} from '@shared/models/settings/auth-method';
import { AuthenticationMethod } from '@shared/models/document/authentication/authentication.method';
import {EnabledAuthMethod} from '@shared/models/settings/enabled-auth-method';
import {Settings} from '@shared/models/settings/settings';
import {SettingsService} from '@services/settings/settings.service';
import {AuthenticationMethod as OutputAuthenticationMethod} from '@shared/models/signrequest/authentication/authentication-method';
import {AuthMethodsComponent} from '@shared/components/auth-methods/auth-methods.component';
import {Signatory} from '@shared/models/document/signatory';
import * as _ from 'lodash';

export type SignatoryAuthMethod = AuthenticationMethod & {
    initials?: string;
    legalLastnamePrefix?: string;
    legalLastname?: string;
    dateofbirth?: string;
    givenName?: string;
    surName?: string;
    birthDate?: string;
    mobilePhone?: string;
    allowedCSCProviders?: string[];
};

@Component({
    selector: 'app-edit-signatory',
    templateUrl: './edit-signatory.component.html',
    styleUrls: ['./edit-signatory.component.scss'],
    standalone: true,
    imports: [FormsModule, ReactiveFormsModule, TranslateModule, AuthMethodsComponent]
})


export class EditSignatoryComponent implements OnInit, OnDestroy {

    protected readonly settingsService = inject(SettingsService);

    readonly settings: Settings;
    readonly enabledAuthenticationMethods: EnabledAuthMethod[] = [];
    disabledAuthenticationMethods: AuthMethod[] = [];

    public signatory!: Signatory;
    public documentUUID!: string;
    public serverMsg: string | null = null;

    public enabledProviders: CscProvider[];
    private cscUtils = new CscUtils();

    readonly nameInputControl = new FormControl('', { validators: [Validators.required], nonNullable: true });
    readonly emailInputControl = new FormControl('', { validators: [Validators.required, Validators.email], nonNullable: true });

    private subscription: Subscription = new Subscription();
    private signrequestService = inject(SignrequestService);
    private authenticationMapper = inject(AuthenticationMapper);
    public readonly bsModalRef: BsModalRef = inject(BsModalRef);

    readonly phoneControl = new FormControl('', { validators: [Validators.pattern(/^[0-9+]*$/)], nonNullable: true });

    public readonly restrictedStates: string[] = ['VIEWING', 'VIEWED', 'VALIDATED', 'DOWNLOAD_ACCESSED', 'DOWNLOADED', 'ERROR', 'REJECTED', 'REPLACED', 'DELEGATED', 'GROUP_COMPLETED', 'AUTHENTICATION_FAILED', 'VALIDATED', 'DOWNLOAD_ACCESSED', 'DOWNLOADED'];

    @Output() saveEvent = new EventEmitter<void>();

    constructor() {
        this.settings = this.settingsService.settings;
        this.enabledAuthenticationMethods = this.settings.enabledAuthenticationMethods.filter(
            am => 'SIGNING' === am.dmtService &&
                !am.isPreview() &&
                !GlobalMethodsArray.includes(am.authenticationMethod as AuthMethod)
        );

        this.enabledProviders = _.cloneDeep(this.cscUtils.enabledProviders);
        const cscMethod: EnabledAuthMethod | undefined = this.enabledAuthenticationMethods.find(value => value.authenticationMethod.toLowerCase() === 'csc');
        if ( cscMethod && cscMethod.enabledProviders ) {
            cscMethod.enabledProviders.forEach(ep  => {
                const provider: CscProvider | undefined = this.enabledProviders.find(value => value.id === ep);
                if ( provider ) {
                    provider.selected = true;
                }
            });
        }
    }

    ngOnInit() {
        if (this.signatory) {

            this.nameInputControl.setValue(this.signatory.name || '');
            this.emailInputControl.setValue(this.signatory.email || '');

            if (this.signatory.authenticationMethods) {
                this.signatory.authenticationMethods.forEach((am: SignatoryAuthMethod) => {
                    const method = am.authenticationMethods?.toUpperCase();
                    const type = am.authType?.toUpperCase();

                    if (type === 'PREVIEW') {
                        if (method === 'SMSTAN' || method === 'SMS') {
                            this.phoneControl.setValue(am.mobilePhone || '');
                        }
                    } else if (type === 'AFTERVIEW') {
                        if (method === 'SMSTAN' || method === 'SMS') {
                            this.phoneControl.setValue(am.mobilePhone || '');
                        } else if (method === 'CSC') {
                            if (am.allowedCSCProviders) {
                                this.enabledProviders.forEach(p => {
                                    p.selected = am.allowedCSCProviders?.includes(p.name) || false;
                                });
                            }
                        }
                    }
                });
            }
        }
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
    }

    isExclusive(methodName: string, type: 'PREVIEW' | 'AFTERVIEW'): boolean {
        if (!methodName) return false;

        const name = methodName.toLowerCase();
        const isRestricted = ExclusiveMethodsArray.some(m => m.toLowerCase() === name);
        if (!isRestricted) return false;

        const activeExclusives: string[] = [];

        if (type === 'PREVIEW') {
            if (this.hasMethod('CSC', 'PREVIEW')) activeExclusives.push('csc');
        } else {
            if (this.hasMethod('MOUSE', 'AFTERVIEW')) activeExclusives.push('mouse');
            if (this.hasMethod('CSC', 'AFTERVIEW')) activeExclusives.push('csc');
        }

        const filteredExclusives = activeExclusives.filter(ex =>
            ExclusiveMethodsArray.some(e => e.toLowerCase() === ex)
        );

        if (filteredExclusives.length === 0) return false;

        return filteredExclusives.some(active =>
            !areMethodsCompatible(active as any, name as any)
        );
    }

    isStateRestricted(): boolean {
        if (!this.signatory || !this.signatory.state) {
            return false;
        }

        return this.restrictedStates.includes(this.signatory.state);
    }

    toggleCscProvider(provider: CscProvider) {
        provider.selected = !provider.selected;
    }

    updateSignatory() {
        const documentUpdate: DocumentUpdate = new DocumentUpdate();
        documentUpdate.documentUUID = this.documentUUID;

        const signatoryUpdate: SignatoryUpdate = new SignatoryUpdate();
        signatoryUpdate.signatoryPublicUUID = this.signatory.publicUUID;

        signatoryUpdate.name = this.nameInputControl.value;
        signatoryUpdate.email = this.emailInputControl.value;

        const activeMethods = (this.signatory.authenticationMethods || []).map((am: any) => {
            const method = am.authenticationMethods?.toUpperCase();
            const type = am.authType?.toUpperCase();
            const updatedAm = { ...am };

            if (type === 'PREVIEW') {
                if (method === 'SMSTAN' || method === 'SMS') {
                    updatedAm.mobilePhone = this.phoneControl.value;
                }
            } else if (type === 'AFTERVIEW') {
                if (method === 'SMSTAN' || method === 'SMS') {
                    updatedAm.mobilePhone = this.phoneControl.value;
                } else if (method === 'CSC') {
                    updatedAm.allowedCSCProviders = this.enabledProviders.filter(p => p.selected).map(p => p.name);
                }
            }
            return updatedAm;
        });

        signatoryUpdate.authenticationMethods = this.authenticationMapper.mapListInputToOutput(activeMethods);
        documentUpdate.signatoryUpdates.push(signatoryUpdate);

        this.subscription.add(this.signrequestService.updateSignatory(documentUpdate).subscribe({
            next: (value) => {
                if (value.message === 'Document successfully updated') {
                    const original = this.bsModalRef.content?.signatory;

                    if (original) {
                        original.name = this.nameInputControl.value;
                        original.email = this.emailInputControl.value;
                        original.authenticationMethods = activeMethods;
                    }

                    this.saveEvent.emit();
                    this.bsModalRef.hide();
                }
            },
            error: (error) => {
                this.serverMsg = error.error?.message || 'An error occurred';
            }
        }));
    }

    hasMethod(methodName: string, type: 'PREVIEW' | 'AFTERVIEW'): boolean {
        return this.signatory?.authenticationMethods?.some((am: any) =>
            am.authenticationMethods?.toUpperCase() === methodName.toUpperCase() &&
            am.authType?.toUpperCase() === type
        ) ?? false;
    }

    areMethodsInvalid(): boolean {
        if (!this.signatory?.authenticationMethods || this.signatory.authenticationMethods.length === 0) {
            return true;
        }
        if (this.hasMethod('SMSTAN', 'PREVIEW') || this.hasMethod('SMS', 'PREVIEW') ||
            this.hasMethod('SMSTAN', 'AFTERVIEW') || this.hasMethod('SMS', 'AFTERVIEW') ) {
            if (!this.phoneControl.value?.trim()) {
                return true;
            }
        }
        if (this.hasMethod('CSC', 'PREVIEW') || this.hasMethod('CSC', 'AFTERVIEW')) {
            if ( this.enabledProviders && this.enabledProviders.filter(p => p.selected).length === 0 ) {
                return true;
            }
        }

        return false;
    }

    toggleAuthentication(method: AuthMethod, preview = false) {
        let baseMethod = method as string;
        let targetType = preview ? 'PREVIEW' : 'AFTERVIEW';

        if (baseMethod.endsWith('_preview')) {
            targetType = 'PREVIEW';
            baseMethod = baseMethod.split('_')[0];
        } else if (preview) {
            targetType = 'PREVIEW';
        }

        if (baseMethod === 'document_anchors') {
            baseMethod = 'documentanchor';
        }

        if (!this.signatory.authenticationMethods) {
            this.signatory.authenticationMethods = [];
        }

        const index = this.signatory.authenticationMethods.findIndex(am =>
            am.authenticationMethods?.toUpperCase() === baseMethod.toUpperCase() &&
            am.authType?.toUpperCase() === targetType
        );

        const turningOn = index === -1;

        if (turningOn) {
            this.signatory.authenticationMethods.push({
                authenticationMethods: baseMethod.toUpperCase(),
                authType: targetType
            } as SignatoryAuthMethod);
        } else {
            this.signatory.authenticationMethods.splice(index, 1);
        }
    }

}

export class DocumentUpdate {
    documentUUID: string | undefined;
    signatoryUpdates: SignatoryUpdate[] = [];
}

export class SignatoryUpdate {
    name: string | undefined;
    email: string | undefined;
    signatoryPublicUUID: string | undefined;
    authenticationMethods: OutputAuthenticationMethod[] = [];
}
